import { Condition } from "components/dynamic-content/CondiitonalGate"
import { LanguageOption, PageOption, TileOrLanguageActions } from "components/dynamic-content/util/dynamicContent"
import { reportErrorMessage } from "../../../utils/ErrorReporting"
import { Asset, Entry } from "contentful"
import {
  ICalendlyLink,
  ITalkToCarrotCountry,
  ITalkToCarrotLanguage,
  ITalkToCarrotLanguagePage,
  ITalkToCarrotSendAMessage,
  ITile,
  ITilePage
} from "contentful-types"
import slugify from "slugify"
import { toCondition } from "./conditionUtils"

enum TileNextActionType {
  TILE_PAGE = "tilePage",
  LANGUAGE_PAGE = "talkToCarrotLanguagePage",
  CALENDLY_LINK = "calendlyLink",
  TALK_TO_CARROT_SEND_A_MESSAGE = "talkToCarrotSendAMessage"
}

const fieldIsValid = (entry: Entry<any>): boolean => {
  return entry && entry.sys.type !== "Link"
}

const getTileTitle = (tile: ITile): string => {
  if (fieldIsValid(tile)) {
    const {
      fields: { display }
    } = tile
    if (fieldIsValid(display)) {
      const {
        fields: { title }
      } = display
      return title
    }
  }
  return null
}

const getLanguageName = (language: ITalkToCarrotLanguage): string => {
  if (fieldIsValid(language)) {
    const {
      fields: { name }
    } = language
    return name
  }
  return null
}

const getPathForTile = ({ tile, baseUrl }: { tile: ITile; baseUrl: string }): string => {
  const title = getTileTitle(tile)
  const slug = title ? slugify(title).toLowerCase() : "unknown"
  return `${baseUrl}/${slug}`
}

const getPathForLanguage = ({ language, baseUrl }: { language: ITalkToCarrotLanguage; baseUrl: string }): string => {
  const languageName = getLanguageName(language)
  const slug = languageName ? slugify(languageName).toLowerCase() : "unknown"
  return `${baseUrl}/${slug}`
}

const getNextTileAction = (
  tile: ITile
): ITilePage | ICalendlyLink | ITalkToCarrotSendAMessage | ITalkToCarrotLanguagePage => {
  const {
    fields: { nextAction }
  } = tile
  return nextAction
}

const getNextLanguageAction = (language: ITalkToCarrotLanguage): ITilePage | ICalendlyLink => {
  const {
    fields: { nextAction }
  } = language
  return nextAction
}

const getNextActionType = (
  nextAction: ITilePage | ICalendlyLink | ITalkToCarrotSendAMessage | ITalkToCarrotLanguagePage
): TileNextActionType => {
  const {
    sys: {
      contentType: {
        sys: { id }
      }
    }
  } = nextAction
  return Object.values(TileNextActionType).find((type) => type == id)
}
const getIconUrl = (icon: Asset): string => {
  if (icon) {
    const {
      fields: { file }
    } = icon
    return file ? file.url : null
  }
  return null
}

const getTileOnClick = ({
  tile,
  actions,
  baseUrl
}: {
  tile: ITile
  actions: TileOrLanguageActions
  baseUrl: string
}): (() => void) => {
  const { gotoPageAction, openCalendlyAction } = actions
  const nextAction = getNextTileAction(tile)

  if (fieldIsValid(nextAction)) {
    switch (getNextActionType(nextAction)) {
      case TileNextActionType.TILE_PAGE:
        return () => gotoPageAction(getPathForTile({ tile, baseUrl }))
      case TileNextActionType.LANGUAGE_PAGE:
        return () => gotoPageAction(getPathForTile({ tile, baseUrl }))
      case TileNextActionType.TALK_TO_CARROT_SEND_A_MESSAGE:
        return () => gotoPageAction(getPathForTile({ tile, baseUrl }))
      case TileNextActionType.CALENDLY_LINK:
        return () => openCalendlyAction((nextAction as ICalendlyLink)?.fields?.url)
    }
  } else {
    return () => gotoPageAction("/404")
  }
}
const getTTCLocationOption = ({ fields: { country, page } }: ITalkToCarrotCountry): PageOption => {
  if (fieldIsValid(country) && fieldIsValid(page)) {
    const {
      fields: { name, alpha2CountryCode }
    } = country
    const {
      sys: { id }
    } = page
    return {
      slug: slugify(name).toLowerCase(),
      label: name,
      value: alpha2CountryCode,
      pageId: id
    }
  } else {
    return null
  }
}

const getTTCLanguageOption = ({
  fields: { name, iso639LanguageCode, nextAction },
  sys: { id }
}: ITalkToCarrotLanguage): LanguageOption => {
  if (fieldIsValid(nextAction)) {
    return {
      label: name,
      value: iso639LanguageCode,
      nextAction: nextAction,
      entryId: id
    }
  } else {
    return null
  }
}

const END_ACTIONS = [
  TileNextActionType.CALENDLY_LINK,
  TileNextActionType.TALK_TO_CARROT_SEND_A_MESSAGE,
  TileNextActionType.LANGUAGE_PAGE
]

const isTileDeadEnd = (tile: ITile, conditionsCheckFn: (conditions: Condition[]) => boolean): boolean => {
  const action = getNextTileAction(tile)
  const type = getNextActionType(action)
  if (END_ACTIONS.includes(type)) {
    return false
  } else if (type === TileNextActionType.TILE_PAGE) {
    const {
      fields: { tiles }
    } = action as ITilePage

    const userMeetsConditions = ({ fields: { conditions } }: ITile): boolean => {
      return !conditions || conditionsCheckFn(conditions.map(toCondition))
    }
    const tileIsNotDeadend = (tile: ITile): boolean => !isTileDeadEnd(tile, conditionsCheckFn)

    const allowedTilesWithEndActions = tiles.filter(userMeetsConditions).filter(tileIsNotDeadend)
    return allowedTilesWithEndActions.length === 0
  } else {
    reportErrorMessage(`Unexpected next action type for Talk to Carrot tile. NextActionType: ${type}`)
    return true
  }
}

export {
  TileNextActionType,
  getTileTitle,
  getLanguageName,
  getPathForTile,
  getPathForLanguage,
  fieldIsValid,
  getNextTileAction,
  getNextLanguageAction,
  getIconUrl,
  getNextActionType,
  getTileOnClick,
  getTTCLocationOption,
  getTTCLanguageOption,
  isTileDeadEnd
}
